import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, Compass, Users } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { SEO } from "../components/SEO";
import { TEAM, FOUNDER_BIO } from "../data/siteData";
import { trackEvent } from "../lib/analytics";

const spring = { type: "spring", stiffness: 200, damping: 20 };

const founder = TEAM.find((t) => t.discipline === "physiotherapy");
const clinicians = TEAM.filter((t) => t !== founder);

const FounderFeature = () => (
  <section data-testid="founder-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={spring}
      className="bg-white rounded-[2.5rem] border border-border p-8 sm:p-12 grid lg:grid-cols-5 gap-10 items-center shadow-sm"
    >
      <div className="lg:col-span-2 relative">
        <div className="absolute -inset-3 bg-teal-soft rounded-[2.5rem] opacity-70 pointer-events-none" />
        <img
          src="/holly-sands.jpg"
          alt="Holly Sands, Founder and Pediatric Physiotherapist at Thrive"
          data-testid="founder-photo"
          className="relative rounded-[2rem] w-full max-w-sm mx-auto aspect-[4/5] object-cover object-[center_15%] shadow-lg shadow-navy/10"
          loading="lazy"
        />
      </div>
      <div className="lg:col-span-3 text-center lg:text-left">
        <span data-testid="founder-eyebrow" className="inline-block bg-lime text-navy font-extrabold text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full">
          Meet Our Founder
        </span>
        <h2 data-testid="founder-name" className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight">
          {founder.name}, {founder.credentials}
        </h2>
        <p data-testid="founder-role" className="mt-1.5 text-sm sm:text-base font-extrabold text-teal-dark">
          {founder.role}
        </p>
        <p data-testid="founder-registration" className="mt-1.5 text-xs font-bold text-navy-light/80">
          {founder.registrations[0]}
        </p>
        <div data-testid="founder-bio" className="mt-5 space-y-4 text-base text-navy-light leading-relaxed max-w-2xl">
          {FOUNDER_BIO.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>
        <Link
          to="/about"
          data-testid="founder-story-link"
          className="mt-7 inline-flex items-center gap-2 bg-white text-navy font-bold px-7 py-3.5 rounded-full border-2 border-border transition-all duration-200 hover:-translate-y-0.5 hover:border-teal hover:text-teal-dark group"
        >
          Our Story
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.5} />
        </Link>
      </div>
    </motion.div>
  </section>
);

const TeamGrid = () => (
  <section data-testid="team-grid-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={spring} className="text-center max-w-2xl mx-auto">
      <span className="inline-flex items-center gap-2 bg-teal-soft text-teal-dark font-extrabold text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full">
        <Users className="w-3.5 h-3.5" /> The Team
      </span>
      <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight">One connected clinical team</h2>
    </motion.div>

    <div className="mt-12 grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
      {clinicians.map((member, i) => (
        <motion.article
          key={member.name}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ ...spring, delay: i * 0.08 }}
          data-testid={`team-card-${i}`}
          className="bg-white rounded-[2rem] border border-border p-8 text-center transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10"
        >
          {member.photo ? (
            <img
              src={member.photo}
              alt={`${member.name}, ${member.role} at Thrive`}
              data-testid={`team-photo-${i}`}
              className="mx-auto w-36 h-36 sm:w-44 sm:h-44 rounded-[2rem] object-cover shadow-lg shadow-navy/10 border-4 border-white"
              style={{ objectPosition: member.photoPos || "center 15%" }}
              loading="lazy"
            />
          ) : (
            <span
              data-testid={`team-avatar-${i}`}
              className={`mx-auto w-20 h-20 rounded-[1.5rem] ${member.color} flex items-center justify-center font-display text-2xl font-semibold text-navy shadow-lg shadow-navy/10`}
            >
              {member.initials}
            </span>
          )}
          <h3 data-testid={`team-name-${i}`} className="mt-5 font-display text-xl font-semibold text-navy">
            {member.name}{member.credentials ? `, ${member.credentials}` : ""}
          </h3>
          <p data-testid={`team-role-${i}`} className="mt-1 text-sm font-extrabold text-teal-dark">
            {member.role}
          </p>
          {member.registrations && member.registrations.length > 0 && (
            <div data-testid={`team-registrations-${i}`} className="mt-3 space-y-1">
              {member.registrations.map((reg) => (
                <p key={reg} className="text-xs font-bold text-navy-light/80 leading-relaxed">{reg}</p>
              ))}
            </div>
          )}
        </motion.article>
      ))}
    </div>
  </section>
);

const TeamCTA = () => (
  <section data-testid="team-cta" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
    <div className="relative overflow-hidden bg-teal-dark rounded-[2.5rem] px-8 py-14 sm:px-16 text-center">
      <div className="absolute -top-16 -left-16 w-56 h-56 bg-white/10 blob-shape pointer-events-none" />
      <div className="absolute -bottom-20 -right-16 w-64 h-64 bg-butter/25 blob-shape-alt pointer-events-none" />
      <span className="relative inline-flex w-14 h-14 rounded-2xl bg-white/10 items-center justify-center mb-5">
        <Compass className="w-7 h-7 text-butter" strokeWidth={2.25} />
      </span>
      <h2 data-testid="team-cta-title" className="relative font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight text-balance">
        Not sure where to start? We're here to help.
      </h2>
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 relative">
        <Link
          to="/services"
          data-testid="team-cta-services-btn"
          className="inline-flex items-center gap-2 text-white font-bold px-8 py-4 rounded-full border-2 border-white/40 transition-all duration-200 hover:-translate-y-1 hover:bg-white/10"
        >
          Explore Our Services
          <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </Link>
        <Link
          to="/contact"
          onClick={() => trackEvent("book_evaluation_click", { location: "team_page", channel: "contact" })}
          data-testid="team-cta-book-btn"
          className="inline-flex items-center gap-2 bg-sunny text-navy font-extrabold px-8 py-4 rounded-full shadow-lg shadow-black/10 transition-all duration-200 hover:-translate-y-1 hover:bg-sunny-dark focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-teal-dark"
        >
          <CalendarCheck className="w-5 h-5" strokeWidth={2.5} />
          Book an Evaluation
        </Link>
      </div>
    </div>
  </section>
);

export default function Team() {
  return (
    <div data-testid="team-page">
      <SEO
        title="Our Team | Thrive Pediatric Therapy, Nassau"
        description="Meet the multidisciplinary pediatric therapy team at Thrive in Nassau, Bahamas - physiotherapy, occupational therapy, speech and language therapy and feeding therapy clinicians working together for your child."
      />
      <PageHeader
        testId="team-header"
        eyebrow="Our Team"
        title="Better together."
        description="Our clinicians work collaboratively to provide thoughtful, individualized and age-appropriate care for children and their families."
      />
      <FounderFeature />
      <TeamGrid />
      <TeamCTA />
    </div>
  );
}
